import { Link, useRouter } from '@tanstack/react-router';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { tokens } from '../api/client';
import { useCurrentUser } from '../api/hooks';
import { LANGUAGES, type Language, setLanguage } from '../i18n';
import { useOnline } from '../lib/use-countdown';
import { IconArrowLeft, IconClose } from './icons';

/**
 * Top bar: back where it makes sense, brand otherwise, plus an offline flag.
 *
 * `wide` must match the page's Screen (review §4.13): the bar's inner column
 * follows the same 800px desktop cap so the back button and title stay on the
 * content's left edge. Below lg it is identical either way.
 *
 * Deliberately NO LanguageSwitch here: language is a once-per-visit choice, so
 * it lives only on the home BrandBar and must not follow the driver through the
 * lot → duration → confirm → pay flow. Inner pages never surface it.
 */
export function TopBar({ title, back = true, wide }: { title?: string; back?: boolean; wide?: boolean }) {
  const router = useRouter();
  const online = useOnline();
  const { t } = useTranslation();

  return (
    <header className="sticky top-0 z-10 border-b border-hairline bg-white/95 backdrop-blur">
      <div
        className={clsx(
          'mx-auto flex w-full max-w-[520px] items-center gap-3 px-[18px] py-3',
          wide && 'lg:max-w-[800px]',
        )}
      >
        {back ? (
          <button
            type="button"
            aria-label={t('common.back')}
            onClick={() => router.history.back()}
            className="-ml-2 flex h-11 w-11 items-center justify-center"
          >
            <IconArrowLeft className="h-5 w-5" />
          </button>
        ) : null}
        <span className="flex-1 truncate text-[17px] font-semibold">{title ?? t('common.brand')}</span>
        {!online ? (
          <span className="rounded-full bg-warning/12 px-2.5 py-1 text-[11px] text-warning">
            {t('common.offline')}
          </span>
        ) : null}
      </div>
    </header>
  );
}

/**
 * Offline notice. Cached lots and prices still render, but the driver has to
 * know the number they are looking at may be stale and that payment needs a
 * connection — silently failing at the pay button would be worse.
 */
export function OfflineBanner() {
  const online = useOnline();
  const { t } = useTranslation();
  if (online) return null;
  return (
    <div className="border-b border-warning/30 bg-warning/8 px-[18px] py-2 text-center text-xs text-warning">
      {t('common.offlineBanner')}
    </div>
  );
}

/**
 * Language toggle. Deliberately a plain two-item control in the header rather
 * than a settings screen — someone who opened this in the wrong language needs
 * it visible on the first screen, not three taps away.
 */
export function LanguageSwitch() {
  const { i18n } = useTranslation();
  const current = i18n.resolvedLanguage?.startsWith('zh') ? 'zh' : 'en';
  return (
    <div className="flex overflow-hidden rounded-full border border-hairline text-[11px]">
      {/* min-h-11 keeps each segment on the 44px touch target (review §5);
          the header row is already 44px tall for the back button, so the
          taller pill does not grow the bar. */}
      {LANGUAGES.map((lang) => (
        <button
          key={lang.code}
          type="button"
          aria-pressed={current === lang.code}
          onClick={() => setLanguage(lang.code as Language)}
          className={`min-h-11 px-3 ${current === lang.code ? 'bg-ink text-white' : 'text-muted'}`}
        >
          {lang.label}
        </button>
      ))}
    </div>
  );
}

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'parkingpay.web.installDismissed';

/**
 * Add-to-home-screen hint. Deliberately a quiet strip at the bottom of the home
 * screen and never a modal — someone mid-payment in a garage must not have the
 * flow interrupted by an install ad.
 */
export function InstallHint() {
  const { t } = useTranslation();
  const [event, setEvent] = useState<InstallPromptEvent | null>(null);
  const [hidden, setHidden] = useState(() => localStorage.getItem(DISMISS_KEY) === '1');

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setEvent(e as InstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  if (!event || hidden) return null;

  return (
    <div className="flex items-center gap-3 rounded-lg border border-hairline p-3">
      <div className="flex-1">
        <p className="text-sm font-semibold">{t('install.title')}</p>
        <p className="text-xs text-muted">{t('install.body')}</p>
      </div>
      {/* Outline, not black: the screen's single black primary belongs to the
          parking flow, and installing is a side offer (review §5). */}
      <button
        type="button"
        className="rounded-lg border-[1.5px] border-hairline px-3 text-xs font-semibold text-accent"
        onClick={() => {
          void event.prompt();
          setHidden(true);
        }}
      >
        {t('install.add')}
      </button>
      <button
        type="button"
        aria-label={t('install.dismiss')}
        className="flex h-11 w-11 items-center justify-center text-muted"
        onClick={() => {
          localStorage.setItem(DISMISS_KEY, '1');
          setHidden(true);
        }}
      >
        <IconClose className="h-4 w-4" />
      </button>
    </div>
  );
}

/** Prompts a reload when a new build is waiting, rather than swapping mid-flow. */
export function BrandBar({ wide }: { wide?: boolean }) {
  const { t } = useTranslation();
  const currentUser = useCurrentUser(tokens.signedIn);
  const hasRecoverableIdentity = Boolean(currentUser.data?.email || currentUser.data?.phoneE164);
  const showSignIn = !tokens.signedIn || (!currentUser.isPending && !hasRecoverableIdentity);
  return (
    <header className="sticky top-0 z-10 border-b border-hairline bg-white/95 backdrop-blur">
      {/* Same wide rule as TopBar: the bar column tracks the page's Screen
          width so brand/history stay aligned with content (review §4.13). */}
      <div
        className={clsx(
          'mx-auto flex w-full max-w-[520px] items-center gap-2 px-[18px] py-3',
          wide && 'lg:max-w-[800px]',
        )}
      >
        {/* No negative letter-spacing on interactive text, and both links
            keep the 44px touch target (review §5). */}
        <Link to="/" className="flex min-h-11 min-w-0 flex-1 items-center truncate text-[17px] font-bold">
          {t('common.brand')}
        </Link>
        <LanguageSwitch />
        <Link
          to="/history"
          className="flex min-h-11 shrink-0 items-center whitespace-nowrap text-sm text-accent"
        >
          {t('common.history')}
        </Link>
        {showSignIn ? (
          <Link
            to="/login"
            search={{ next: undefined }}
            className="flex min-h-11 shrink-0 items-center whitespace-nowrap text-sm font-semibold text-accent"
          >
            {t('common.signIn')}
          </Link>
        ) : null}
      </div>
    </header>
  );
}
